import * as THREE from 'three'
import type { GroundPlane } from './ground'

/**
 * The analytic floor, drawn.
 *
 * Once the floor is a plane rather than sensed geometry there is nothing left
 * in the depth mesh to occlude with or to look at down there, so the plane
 * itself has to do both jobs: write depth so a box that slides under the real
 * floor disappears, and — with the skin on — show where the solver thinks the
 * ground is, since that is now an assertion rather than a measurement.
 */

/** Far past any room this runs in, so the plane reads as unbounded. */
const SIZE = 30

/** One grid square per metre, which is what makes the wireframe legible. */
const SEGMENTS = 30

const GROUND_COLOUR = 0x2a5266

const UP = new THREE.Vector3(0, 1, 0)
const normal = new THREE.Vector3()

export type FloorSkin = 'off' | 'wire' | 'solid'

export class FloorPlane {
  private readonly geometry: THREE.PlaneGeometry
  private readonly occluder: THREE.Mesh
  private readonly skin: THREE.Mesh
  private readonly solid: THREE.MeshBasicMaterial
  private readonly wire: THREE.MeshBasicMaterial

  constructor(scene: THREE.Scene) {
    this.geometry = new THREE.PlaneGeometry(SIZE, SIZE, SEGMENTS, SEGMENTS)
    this.geometry.rotateX(-Math.PI / 2)

    this.occluder = new THREE.Mesh(
      this.geometry,
      new THREE.MeshBasicMaterial({ colorWrite: false, depthWrite: true })
    )
    this.occluder.frustumCulled = false
    this.occluder.renderOrder = -1
    this.occluder.visible = false
    scene.add(this.occluder)

    this.solid = new THREE.MeshBasicMaterial({
      color: GROUND_COLOUR,
      transparent: true,
      opacity: 0.25,
      side: THREE.DoubleSide,
      depthWrite: false,
    })
    this.wire = new THREE.MeshBasicMaterial({
      color: GROUND_COLOUR,
      wireframe: true,
      transparent: true,
      opacity: 0.6,
      depthWrite: false,
    })

    this.skin = new THREE.Mesh(this.geometry, this.solid)
    this.skin.frustumCulled = false
    this.skin.visible = false
    scene.add(this.skin)
  }

  hide(): void {
    this.occluder.visible = false
    this.skin.visible = false
  }

  /** Places both meshes on `plane`. The skin follows the demo's skin cycle. */
  sync(plane: GroundPlane, skin: FloorSkin): void {
    normal.set(-plane.slopeX, 1, -plane.slopeZ).normalize()
    this.occluder.quaternion.setFromUnitVectors(UP, normal)
    this.occluder.position.set(plane.originX, plane.height, plane.originZ)

    this.skin.quaternion.copy(this.occluder.quaternion)
    this.skin.position.copy(this.occluder.position)
    this.skin.material = skin === 'wire' ? this.wire : this.solid

    this.occluder.visible = true
    this.skin.visible = skin !== 'off'
  }

  dispose(): void {
    this.occluder.removeFromParent()
    ;(this.occluder.material as THREE.Material).dispose()
    this.skin.removeFromParent()
    this.solid.dispose()
    this.wire.dispose()
    this.geometry.dispose()
  }
}
